import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { LogOut, Sun, Moon, Monitor } from "lucide-react";
import { useAuthActions } from "@convex-dev/auth/react";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { api } from "@/lib/convex-api";
import { formatUserFacingError } from "@/lib/errorMessages";
import { useThemeStore } from "@/stores/theme-store";
import { toast } from "sonner";

interface UserSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserSettings = ({ open, onOpenChange }: UserSettingsProps) => {
  const user = useQuery(api.users.me);
  const updateProfile = useMutation(api.users.updateProfile);
  const { signOut } = useAuthActions();
  const [displayName, setDisplayName] = useState(user?.name ?? "User");
  const [aiProvider, setAiProvider] = useState("openai");
  const { theme, setTheme } = useThemeStore();

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.name);
    setAiProvider(user.llmProvider ?? "openai");
  }, [user]);

  const saveProfile = async () => {
    try {
      await updateProfile({
        name: displayName.trim(),
        llmProvider: aiProvider === "anthropic" ? "anthropic" : "openai",
      });
    } catch (error) {
      toast.error(formatUserFacingError(error, "Failed to save settings."));
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange} title="Settings">
      <div className="space-y-5">
        {/* Profile */}
        <section className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">Profile</h3>
          <div className="flex items-center gap-3">
            <Avatar size="lg">
              <AvatarFallback>JD</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="text-sm font-medium text-foreground">
                {user?.name ?? displayName}
              </p>
              <p className="text-xs text-muted-foreground">
                {user?.email ?? ""}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="display-name">Display name</Label>
            <Input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onBlur={() => void saveProfile()}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs font-normal">
              Email
            </Label>
            <p className="text-sm text-foreground">{user?.email ?? ""}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Email is tied to your sign-in method and cannot be changed here.
          </p>
        </section>

        <Separator />

        {/* Preferences */}
        <section className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Preferences</h3>

          {/* AI Model Provider */}
          <div className="space-y-2">
            <Label className="text-xs">AI Model Provider</Label>
            <ToggleGroup
              type="single"
              variant="outline"
              value={aiProvider}
              onValueChange={(v) => {
                if (v) setAiProvider(v);
                void saveProfile();
              }}
            >
              <ToggleGroupItem value="openai" className="text-xs">
                OpenAI
              </ToggleGroupItem>
              <ToggleGroupItem value="anthropic" className="text-xs">
                Anthropic
              </ToggleGroupItem>
            </ToggleGroup>
            <p className="text-xs text-muted-foreground">
              Sets your default AI provider for chat. You can also switch
              providers per-conversation in the chat panel.
            </p>
          </div>

          {/* Theme */}
          <div className="space-y-2">
            <Label className="text-xs">Theme</Label>
            <ToggleGroup
              type="single"
              variant="outline"
              value={theme}
              onValueChange={(v) => {
                if (v) setTheme(v as "system" | "light" | "dark");
              }}
            >
              <ToggleGroupItem value="system" className="text-xs gap-1.5">
                <Monitor className="size-3" />
                System
              </ToggleGroupItem>
              <ToggleGroupItem value="light" className="text-xs gap-1.5">
                <Sun className="size-3" />
                Light
              </ToggleGroupItem>
              <ToggleGroupItem value="dark" className="text-xs gap-1.5">
                <Moon className="size-3" />
                Dark
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </section>

        <Separator />

        {/* Account */}
        <section className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">Account</h3>
          <div className="flex items-center justify-between">
            <Button
              variant="secondary"
              size="sm"
              className="text-xs"
              onClick={() => {
                void signOut().finally(() => {
                  globalThis.location.assign("/login");
                });
              }}
            >
              <LogOut className="size-3" />
              Sign Out
            </Button>
          </div>
        </section>
      </div>
    </ResponsiveDialog>
  );
};
